import { useEffect } from 'react';
import { HandCard, type HandCardData } from '../HandCard.js';
import type { OnFieldCardView } from './AflView.js';

/** Project an on-field card into the hand-card shape the preview/inspect reads. */
function toHandCard(item: OnFieldCardView): HandCardData {
  const c = item.card;
  return { id: item.cardId, kind: c?.kind ?? 'player', name: c?.name, position: c?.position, stats: c?.stats };
}

const ROLE_BADGE: Record<OnFieldCardView['role'], { label: string; cls: string }> = {
  carrier: { label: '🏉 has the ball', cls: 'border-ec-brass/70 bg-[rgba(245,168,28,0.15)] text-ec-brass' },
  support: { label: 'in play', cls: 'border-ec-line bg-ec-ground/60 text-ec-bone' },
  beaten: { label: 'beaten', cls: 'border-ec-line bg-ec-ground/60 text-ec-bone-dim' },
};

export type PositionInspectorProps = {
  /** The clicked position — its viewer-facing name and engine zone. */
  position: { name: string; line: string; lane: string };
  /** Every card standing on the ground (possessor frame, same as `position`). */
  onField: OnFieldCardView[];
  userSeatId: string | null;
  seatLabels: Record<string, string>;
  /** Zoom a single card (the shared inspect modal). */
  onPreview: (card: HandCardData) => void;
  onClose: () => void;
};

/**
 * "Who's at this position?" — tap a field cell and see the cards each COACH has
 * standing there, grouped by coach, with the carrier / beaten state called out.
 * Cards persist on the ground until the next stoppage (owner rule #3), so this
 * is how you read the contest that's set up at a spot. Clicking a card zooms it
 * (and offers any legal play) through the shared preview modal.
 */
export function PositionInspector({
  position,
  onField,
  userSeatId,
  seatLabels,
  onPreview,
  onClose,
}: PositionInspectorProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const here = onField.filter((c) => c.line === position.line && c.lane === position.lane);
  // Group by coach (seat); the viewer's own coach first.
  const seats = Array.from(new Set(here.map((c) => c.seatId))).sort((a, b) =>
    a === userSeatId ? -1 : b === userSeatId ? 1 : 0,
  );
  const coachName = (seatId: string) =>
    seatId === userSeatId ? 'You' : (seatLabels[seatId] ?? 'Opponent');

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ec-ground/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="my-auto w-full max-w-md animate-pop-in rounded-xl border border-ec-line bg-ec-panel/95 p-4 shadow-ec-panel motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-ec-brass">In play at</p>
            <h2 className="font-display text-lg font-bold uppercase tracking-tight text-ec-face">{position.name || 'this position'}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-ec-line px-2.5 py-1 font-display text-xs uppercase tracking-wide text-ec-bone transition hover:border-ec-bone"
          >
            Close
          </button>
        </div>

        {here.length === 0 ? (
          <p className="rounded-md border border-ec-line bg-ec-ground/60 px-3 py-6 text-center text-sm text-ec-bone-dim">
            No players standing here yet. Cards hold their ground from a passage of play until the next
            stoppage.
          </p>
        ) : (
          <div className="space-y-3">
            {seats.map((seatId) => {
              const mine = seatId === userSeatId;
              return (
                <div key={seatId}>
                  <p
                    className={`mb-1.5 font-display text-[11px] font-bold uppercase tracking-wider ${
                      mine ? 'text-ec-ruck' : 'text-ec-oxblood-2'
                    }`}
                  >
                    {coachName(seatId)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {here
                      .filter((c) => c.seatId === seatId)
                      .map((item) => {
                        const badge = ROLE_BADGE[item.role];
                        return (
                          <div key={item.cardId} className="flex flex-col items-center gap-1">
                            <div className={item.role === 'beaten' ? 'opacity-60 saturate-50' : ''}>
                              <HandCard
                                card={toHandCard(item)}
                                size="sm"
                                onClick={() => onPreview(toHandCard(item))}
                              />
                            </div>
                            <span
                              className={`rounded-full border px-1.5 py-px text-[9px] font-semibold ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 border-t border-ec-line pt-2 font-serif text-[11px] italic text-ec-bone-dim">
          Tap a card to zoom in — and to use it if it has a legal play right now.
        </p>
      </div>
    </div>
  );
}
